import { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Globe,
  GraduationCap,
  HelpCircle,
  Info,
  ListFilter,
  Mail,
  MapPin,
  Monitor,
  Moon,
  Palette,
  Percent,
  Phone,
  Sun,
  UserRound,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import type { StudentProfile } from '@/types/student';
import {
  ITEMS_PER_PAGE_OPTIONS,
  useStudentPreferences,
  type StudentPreferences,
} from '../store/preferences.store';

interface StudentSettingsProps {
  profile: StudentProfile | null;
}

/** Light or dark. There is no third mode — `useTheme` stores one of these two. */
const THEME_CHOICES = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
] as const satisfies readonly { value: 'light' | 'dark'; label: string; icon: LucideIcon }[];

/**
 * The display toggles, as one table.
 *
 * A row each rather than three near-identical blocks of markup: the switch, its
 * label and its hint were copied out three times, so a fix to the switch had to
 * be made three times (CLAUDE.md §3). The `key` is the store's own, which is
 * what keeps a toggle from being drawn for a preference nothing reads.
 */
const DISPLAY_TOGGLES = [
  {
    key: 'showSubjectIcons',
    icon: GraduationCap,
    label: 'Subject icons',
    hint: 'The coloured subject tiles on your timetable.',
  },
  {
    key: 'showTeacherName',
    icon: UserRound,
    label: 'Teacher names',
    hint: 'Who takes the lesson, and who put a notice up.',
  },
  {
    key: 'showMarksPercentage',
    icon: Percent,
    label: 'Percentages on marks',
    hint: 'The percentage column beside your scores. Your marks stay either way.',
  },
] as const satisfies readonly {
  key: keyof Pick<
    StudentPreferences,
    'showSubjectIcons' | 'showTeacherName' | 'showMarksPercentage'
  >;
  icon: LucideIcon;
  label: string;
  hint: string;
}[];

/**
 * The child's own settings.
 *
 * What this page offers is now exactly what the panel honours: the theme, how
 * many rows a list shows, and three display toggles the timetable, the notices
 * and the marks table read. Everything else it used to offer — a language with
 * no translations behind it, a time zone over date helpers fixed to IST, a
 * List/Grid/Card view where only a list exists, a font size against a type
 * scale written in pixels, a week start day over a timetable whose columns are
 * the institute's working days, and a two-factor row for a feature that exists
 * nowhere in the product — wrote to `localStorage` and was then ignored by
 * every screen. A setting that changes nothing is worse than an absent one: the
 * reader believes they have changed something.
 *
 * The preferences themselves live in `store/preferences.store`, because the
 * screens that honour them need to read the same values this page writes.
 */

export function StudentSettings({ profile }: StudentSettingsProps) {
  /**
   * The theme is read from `useTheme` rather than kept a second time here.
   * This page used to hold its own copy, so the tiles and the toggle in the top
   * bar could disagree about which theme was on.
   */
  const { theme, setTheme } = useTheme();

  const itemsPerPage = useStudentPreferences((state) => state.itemsPerPage);
  const showSubjectIcons = useStudentPreferences((state) => state.showSubjectIcons);
  const showTeacherName = useStudentPreferences((state) => state.showTeacherName);
  const showMarksPercentage = useStudentPreferences((state) => state.showMarksPercentage);
  const setPreference = useStudentPreferences((state) => state.set);
  // Modal dialog states
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Their own institute, their own teacher, their own class — never a name
  // baked into the build (CLAUDE.md §4). The fallbacks here used to be
  // "Bright Scholars Academy" and "Mrs. Sarah Jenkins".
  const instituteName = profile?.institute.name || 'Your school';
  const contact = profile?.institute.contact ?? null;
  const classTeacher = profile?.classTeacher ?? null;
  const className = profile?.section?.displayName ?? null;

  return (
    <div className="flex flex-col gap-5 animate-fade-in max-w-[1400px] mx-auto w-full ">
      {/* 1. TOP HERO BANNER: "Make it yours!" */}
      {/* <div className="relative overflow-hidden rounded-[24px] bg-[linear-gradient(115deg,#161042_0%,#23175c_52%,#140d3d_100%)] dark:bg-[#0e1035] border border-indigo-900/40 dark:border-[#1e234e] px-6 py-2  shadow-xl text-white">
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        
          <div className="flex flex-col gap-2 max-w-xs sm:max-w-sm">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
              Make it yours!
            </h2>
            <p className="text-xs sm:text-sm text-[#b8c2e6] dark:text-[#8892b4] font-medium leading-relaxed">
              Personalize your settings to get the best learning experience.
            </p>
          </div>

         
          <div className="relative flex items-center justify-center mx-auto md:mx-0 py-1">
            <div className="absolute -left-12 top-0 text-[#8592ff] opacity-90">
              <GraduationCap className="h-7 w-7 -rotate-12 stroke-[1.5]" />
            </div>
            <div className="absolute -right-10 top-0 text-[#fbbf24] opacity-95">
              <Lightbulb className="h-7 w-7 rotate-12 stroke-[1.5]" />
            </div>

            <img
              src="/student-overview/student-hero.png"
              alt="Student Character"
              className="h-32 sm:h-36 w-auto object-contain drop-shadow-[0_12px_24px_rgba(0,0,0,0.45)]"
            />
          </div>

         
          <div className="flex flex-col items-start md:items-end justify-center md:text-right max-w-xs md:ml-auto">
            <p className="text-xs sm:text-[13px] text-[#d6defe] dark:text-[#c5cef0] italic leading-relaxed">
              “Small settings make a big difference in your learning journey.”
            </p>
            <div className="h-1 w-12 bg-[#5839ec] rounded-full mt-2.5" />
          </div>
        </div>
      </div> */}

      {/* 2. ROW 1: Theme | Lists ------------------------------------------
          Two cards, not three. "Language & Region" offered four languages the
          product has no translations for and four time zones over date helpers
          deliberately fixed to Asia/Kolkata — a register belongs to an Indian
          institute day, so a child setting Eastern Time would only have been
          shown the wrong one. "Font Size" scaled nothing: the type scale in
          `tailwind.config.js` is written in pixels. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Theme */}
        <div className="card p-5 bg-white dark:bg-[#0b0e27] border border-slate-200/90 dark:border-[#1b2046] rounded-[24px] shadow-sm flex flex-col gap-5 transition-colors">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-[16px] bg-[#5839ec]/10 text-[#5839ec] dark:bg-[#231c54] dark:text-[#8a74ff] shrink-0 border border-[#5839ec]/20 dark:border-[#352c74]">
              <Palette className="h-5 w-5" />
            </div>
            <div className="flex flex-col">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">Theme</h3>
              <p className="text-xs text-slate-500 dark:text-[#8892b4] mt-0.5 leading-snug">
                Light or dark, across the whole portal. The same switch sits in the top bar.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2.5 pt-1" role="radiogroup" aria-label="Theme">
            {THEME_CHOICES.map((choice) => {
              const isActive = theme === choice.value;

              return (
                <button
                  key={choice.value}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => setTheme(choice.value)}
                  className={cn(
                    'relative flex flex-col items-center justify-center gap-2 p-3.5 rounded-[16px] border text-center transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5839ec]',
                    isActive
                      ? 'border-[#5839ec] bg-[#5839ec]/10 dark:bg-[#231c54]'
                      : 'border-slate-200 dark:border-[#1e244f] bg-slate-50 dark:bg-[#070a1e] hover:border-slate-300 dark:hover:border-[#2b336e]',
                  )}
                >
                  {isActive && (
                    <span className="absolute top-2 right-2 h-4 w-4 rounded-full bg-[#5839ec] text-white grid place-items-center" aria-hidden>
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                  )}
                  <choice.icon
                    className={cn(
                      'h-5 w-5',
                      isActive ? 'text-[#5839ec] dark:text-[#a78bfa]' : 'text-slate-400 dark:text-[#727c9e]',
                    )}
                  />
                  <span
                    className={cn(
                      'text-sm font-bold',
                      isActive ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-[#727c9e]',
                    )}
                  >
                    {choice.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Lists */}
        <div className="card p-5 bg-white dark:bg-[#0b0e27] border border-slate-200/90 dark:border-[#1b2046] rounded-[24px] shadow-sm flex flex-col gap-5 transition-colors">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-[16px] bg-[#5839ec]/10 text-[#5839ec] dark:bg-[#231c54] dark:text-[#8a74ff] shrink-0 border border-[#5839ec]/20 dark:border-[#352c74]">
              <ListFilter className="h-5 w-5" />
            </div>
            <div className="flex flex-col">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">Lists</h3>
              <p className="text-xs text-slate-500 dark:text-[#8892b4] mt-0.5 leading-snug">
                How much of a long list you see before paging.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="items-per-page"
              className="text-sm font-semibold tracking-wider text-slate-500 dark:text-[#727c9e]"
            >
              Items per page
            </label>
            <div className="relative">
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={(event) => setPreference('itemsPerPage', Number(event.target.value))}
                className="w-full appearance-none rounded-[12px] bg-slate-50 dark:bg-[#070a1e] border border-slate-200 dark:border-[#1e244f] px-3.5 pr-8 py-2.5 text-sm font-semibold text-slate-800 dark:text-white focus:border-[#5839ec] focus:outline-none cursor-pointer"
              >
                {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-[#8892b4] pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* 3. ROW 2: what each screen shows ---------------------------------
          Three toggles, each read by a screen: the timetable drops its subject
          icons and its teacher line, and the marks table drops its percentage
          column. "Default View" and "Week Start Day" are gone — there is no
          grid or card view to choose, and the timetable's columns are the
          institute's own working days rather than a preference. */}
      <div className="card p-5 bg-white dark:bg-[#0b0e27] border border-slate-200/90 dark:border-[#1b2046] rounded-[24px] shadow-sm flex flex-col gap-2 transition-colors">
        <div className="flex items-start gap-3 border-b border-slate-100 dark:border-[#1b2046] pb-1">
          <div className="p-2.5 rounded-[16px] bg-[#5839ec]/10 text-[#5839ec] dark:bg-[#231c54] dark:text-[#8a74ff] shrink-0 border border-[#5839ec]/20 dark:border-[#352c74]">
            <Monitor className="h-5 w-5" />
          </div>
          <div className="flex flex-col">
            <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
              What your screens show
            </h3>
            <p className="text-xs text-slate-500 dark:text-[#8892b4] mt-0.5">
              Turn off anything you would rather not see. Nothing is hidden from your teachers.
            </p>
          </div>
        </div>

        <div className="flex flex-col divide-y divide-slate-100 dark:divide-[#1b2046]">
          {DISPLAY_TOGGLES.map((toggle) => {
            const isOn = {
              showSubjectIcons,
              showTeacherName,
              showMarksPercentage,
            }[toggle.key];

            return (
              <div key={toggle.key} className="flex items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="p-2 rounded-[12px] bg-slate-100 dark:bg-[#181d42] text-[#5839ec] dark:text-[#8a74ff] shrink-0" aria-hidden>
                    <toggle.icon className="h-4 w-4" />
                  </span>
                  <span className="flex flex-col min-w-0">
                    <span className="text-sm sm:text-base font-bold text-slate-700 dark:text-white">
                      {toggle.label}
                    </span>
                    <span className="text-2xs sm:text-sm text-slate-500 dark:text-[#727c9e]">
                      {toggle.hint}
                    </span>
                  </span>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={isOn}
                  aria-label={toggle.label}
                  onClick={() => setPreference(toggle.key, !isOn)}
                  className={cn(
                    'relative h-6 w-11 rounded-full transition-colors shrink-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5839ec] focus-visible:ring-offset-2',
                    isOn ? 'bg-[#5839ec]' : 'bg-slate-300 dark:bg-[#1e244f]',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                      isOn ? 'translate-x-5' : 'translate-x-0',
                    )}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* 4. Help & Support */}
        <div className="card p-5 bg-white dark:bg-[#0b0e27] border border-slate-200/90 dark:border-[#1b2046] rounded-[24px] shadow-sm flex flex-col gap-2 transition-colors">
          <div className="flex items-start gap-3 border-b border-slate-100 dark:border-[#1b2046] pb-1">
            <div className="p-2.5 rounded-[16px] bg-[#5839ec]/10 text-[#5839ec] dark:bg-[#231c54] dark:text-[#8a74ff] shrink-0 border border-[#5839ec]/20 dark:border-[#352c74]">
              <HelpCircle className="h-5 w-5" />
            </div>
            <div className="flex flex-col">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">Help & Support</h3>
              <p className="text-xs text-slate-500 dark:text-[#8892b4] mt-0.5">
                Get help or contact the school support team.
              </p>
            </div>
          </div>

          <div className="flex flex-col divide-y divide-slate-100 dark:divide-[#1b2046]">
            {/* View Help Center */}
            <div
              onClick={() => setIsHelpModalOpen(true)}
              className="flex items-center justify-between gap-3 py-3 hover:bg-slate-50 dark:hover:bg-white/[0.02] -mx-2 px-2 rounded-[12px] transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-[12px] bg-slate-100 dark:bg-[#181d42] text-[#5839ec] dark:text-[#8a74ff] shrink-0 group-hover:bg-[#5839ec]/15 dark:group-hover:bg-[#231c54] transition-colors">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm sm:text-base font-bold text-slate-700 dark:text-white group-hover:text-[#5839ec] dark:group-hover:text-[#a78bfa] transition-colors">
                    View Help Center
                  </span>
                  <span className="text-2xs sm:text-sm text-slate-500 dark:text-[#727c9e]">
                    Find answers to common questions.
                  </span>
                </div>
              </div>

              <ChevronRight className="h-4 w-4 text-slate-400 dark:text-[#727c9e] group-hover:text-slate-700 dark:group-hover:text-white group-hover:translate-x-0.5 transition-all" />
            </div>

            {/* Contact School */}
            <div
              onClick={() => setIsContactModalOpen(true)}
              className="flex items-center justify-between gap-3 py-3 hover:bg-slate-50 dark:hover:bg-white/[0.02] -mx-2 px-2 rounded-[12px] transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-[12px] bg-slate-100 dark:bg-[#181d42] text-[#5839ec] dark:text-[#8a74ff] shrink-0 group-hover:bg-[#5839ec]/15 dark:group-hover:bg-[#231c54] transition-colors">
                  <Phone className="h-4 w-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm sm:text-base font-bold text-slate-700 dark:text-white group-hover:text-[#5839ec] dark:group-hover:text-[#a78bfa] transition-colors">
                    Contact School
                  </span>
                  <span className="text-2xs sm:text-sm text-slate-500 dark:text-[#727c9e]">
                    Reach out to your school administration.
                  </span>
                </div>
              </div>

              <ChevronRight className="h-4 w-4 text-slate-400 dark:text-[#727c9e] group-hover:text-slate-700 dark:group-hover:text-white group-hover:translate-x-0.5 transition-all" />
            </div>
          </div>
        </div>

      {/* 5. FOOT
          No "Save Changes" button: it sat beside the words "Your settings are
          saved automatically", wrote the same object back to `localStorage` and
          toasted. Every control on this page applies the moment it is touched,
          which is what the line says. */}
      <div className="flex items-center gap-2 pt-4 border-t border-slate-200 dark:border-[#1b2046] text-xs text-slate-500 dark:text-[#727c9e]">
        <Info className="h-4 w-4 text-slate-400 dark:text-[#727c9e] shrink-0" aria-hidden />
        <span>Saved on this device as you change them.</span>
      </div>

      {/* MODAL 3: HELP CENTER */}
      <Modal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title="Student Portal Help Center"
        description="Frequently asked questions and guides for students."
        size="lg"
      >
        <div className="flex flex-col gap-3.5 py-1">
          {[
            {
              q: 'How do I submit homework assignments?',
              a: 'Go to the "Homework & Tasks" section from the sidebar, click on any active assignment, and upload your worksheet file (PDF/Image) or paste your project link before the due date.',
            },
            {
              q: 'Where can I watch recorded class videos?',
              a: 'Navigate to "Recorded Classes" in the sidebar menu. You can browse lessons by subject, stream lecture videos, and download study notes.',
            },
            {
              q: 'How do I check my exam marks and progress?',
              a: 'Visit the "Exam Marks & Reports" tab to view subject-wise scores, percentage breakdowns, teacher remarks, and performance charts.',
            },
            {
              q: 'What should I do if I forget my login password?',
              a: 'Click "Forgot Password" on the sign-in screen, or request your class teacher / school office to reset your portal password.',
            },
          ].map((faq, idx) => (
            <div key={idx} className="p-3.5 rounded-[14px] bg-surface-2 border border-line">
              <h4 className="text-xs sm:text-sm font-bold text-ink">{faq.q}</h4>
              <p className="text-2xs sm:text-xs text-ink-2 mt-1 leading-relaxed">{faq.a}</p>
            </div>
          ))}

          <div className="flex justify-end pt-2">
            <Button variant="primary" onClick={() => setIsHelpModalOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* MODAL 4: CONTACT SCHOOL */}
      <Modal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="Contact School Administration"
        description="Official communication channels for your school."
      >
        <div className="flex flex-col gap-4 py-2 text-xs sm:text-sm">
          {/*
            The institute's own record, read from the child's profile. This card
            used to print "+91 (080) 2345-6789" and "support@schoolportal.edu.in"
            for every school on the platform — a number a child would actually
            have rung (CLAUDE.md §4). Each line is dropped when the institute has
            not given it, rather than filled in.
          */}
          <div className="p-4 rounded-[16px] bg-surface-2 border border-line flex flex-col gap-3">
            <span className="font-extrabold text-base text-ink">{instituteName}</span>

            {contact ? (
              <>
                <span className="flex items-start gap-2.5 text-ink-2">
                  <MapPin className="h-4 w-4 text-accent shrink-0 mt-0.5" aria-hidden />
                  <span>{contact.address}</span>
                </span>

                {contact.phone && (
                  <a
                    href={`tel:${contact.phone.replace(/\s+/g, '')}`}
                    className="flex items-center gap-2.5 text-ink-2 hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
                  >
                    <Phone className="h-4 w-4 text-accent shrink-0" aria-hidden />
                    <span>{contact.phone}</span>
                  </a>
                )}

                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-2.5 text-ink-2 hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
                >
                  <Mail className="h-4 w-4 text-accent shrink-0" aria-hidden />
                  <span className="truncate">{contact.email}</span>
                </a>

                {contact.website && (
                  <a
                    href={contact.website}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-2.5 text-ink-2 hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
                  >
                    <Globe className="h-4 w-4 text-accent shrink-0" aria-hidden />
                    <span className="truncate">{contact.website}</span>
                  </a>
                )}
              </>
            ) : (
              <span className="text-ink-3">
                Your school has not published its contact details here yet. Ask at the office, or
                your class teacher.
              </span>
            )}
          </div>

          {/* Only once the office has placed them in a class with a teacher. */}
          {classTeacher && (
            <div className="p-3.5 rounded-[14px] bg-surface-2 border border-line flex items-center justify-between gap-3">
              <span className="flex flex-col min-w-0">
                <span className="text-2xs uppercase tracking-wider text-ink-3 font-bold">
                  Class teacher
                </span>
                <span className="text-xs font-bold text-ink truncate">{classTeacher}</span>
              </span>
              {className && (
                <span className="text-xs font-bold text-accent bg-accent-soft px-2.5 py-1 rounded-full border border-accent-line shrink-0">
                  {className}
                </span>
              )}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="primary" onClick={() => setIsContactModalOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
